import { zodResolver } from '@hookform/resolvers/zod';
import { Slido } from '@livekatsomo/models';
import { TextField } from '@livekatsomo/web/ui-components/react-hook-form-components';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { useTranslation } from 'next-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * A schema for the Slido form.
 */
const formSchema = z.object({
  slidoId: z.string().nonempty("Slido ID can't be empty"),
  type: z.literal('event'),
});

/**
 * Props for the SlidoForm component.
 */
export interface SlidoFormProps {
  /**
   * The current Slido object to be edited. If null, a new Slido object will be created.
   */
  slido?: Slido | null;
  /**
   * A function that will be called when the form is submitted. It will receive the updated Slido object as an argument.
   */
  onSubmit: (values: Slido) => Promise<void>;
  /**
   * A function that will be called when the form is closed.
   */
  onClose: () => void;
}

/**
 * Renders a form for creating or editing a Slido object.
 * @returns  - The SlidoForm component.
 */
export function SlidoForm({ slido, onSubmit, onClose }: SlidoFormProps) {
  const { t } = useTranslation();
  const initialValues = {
    type: 'event',
    slidoId: slido?.slidoId || '',
  };

  const methods = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
    mode: 'onChange',
  });

  const {
    formState: { isDirty, isSubmitting, isValid },
    reset,
    handleSubmit,
  } = methods;

  const submit = async (values: typeof initialValues) => {
    const data = formSchema.parse(values);
    await onSubmit(data);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submit)} onReset={() => reset()}>
        <DialogContent>
          <TextField name="slidoId" label={t('Slido ID')} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('Cancel')}</Button>
          <Button type="reset" disabled={isSubmitting || !isDirty}>
            {t('Reset')}
          </Button>
          <LoadingButton
            type="submit"
            disabled={!isValid || isSubmitting || !isDirty}
            loading={isSubmitting}
          >
            {t('Save')}
          </LoadingButton>
        </DialogActions>
      </form>
    </FormProvider>
  );
}
